import { useState } from "react";

interface PlatformNotice {
  id: string;
  title: string;
  content: string;
  level: "info" | "warning" | "urgent";
  targetPlans: string[];
  status: "published" | "draft";
  publishedAt?: string;
  createdAt: string;
  readCount: number;
}

const levelConfig = {
  info: { label: "普通通知", icon: "ri-notification-3-line", color: "bg-gray-100 text-gray-600", badge: "bg-gray-100 text-gray-600" },
  warning: { label: "注意事项", icon: "ri-alert-line", color: "bg-amber-50 text-amber-600", badge: "bg-amber-100 text-amber-700" },
  urgent: { label: "紧急公告", icon: "ri-error-warning-line", color: "bg-red-50 text-red-600", badge: "bg-red-100 text-red-700" },
};

export default function PlatformAnnouncement() {
  const [notices, setNotices] = useState<PlatformNotice[]>([]);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedNotice, setSelectedNotice] = useState<PlatformNotice | null>(null);
  const [newNotice, setNewNotice] = useState({
    title: "",
    content: "",
    level: "info" as PlatformNotice["level"],
    targetPlans: ["trial", "basic", "standard", "premium"] as string[],
  });
  const [publishing, setPublishing] = useState(false);

  const handlePublish = (id: string) => {
    setPublishing(true);
    setTimeout(() => {
      setNotices((p) =>
        p.map((n) =>
          n.id === id
            ? { ...n, status: "published", publishedAt: new Date().toLocaleString("zh-CN") }
            : n
        )
      );
      if (selectedNotice?.id === id) {
        setSelectedNotice((p) =>
          p ? { ...p, status: "published", publishedAt: new Date().toLocaleString("zh-CN") } : null
        );
      }
      setPublishing(false);
    }, 700);
  };

  const handleCreate = () => {
    if (!newNotice.title || !newNotice.content) return;
    const n: PlatformNotice = {
      id: `pn${Date.now()}`,
      title: newNotice.title,
      content: newNotice.content,
      level: newNotice.level,
      targetPlans: newNotice.targetPlans,
      status: "draft",
      createdAt: new Date().toISOString().slice(0, 10),
      readCount: 0,
    };
    setNotices((p) => [n, ...p]);
    setShowCreate(false);
    setNewNotice({ title: "", content: "", level: "info", targetPlans: ["trial", "basic", "standard", "premium"] });
  };

  const toggleTargetPlan = (plan: string) => {
    setNewNotice((p) => ({
      ...p,
      targetPlans: p.targetPlans.includes(plan)
        ? p.targetPlans.filter((x) => x !== plan)
        : [...p.targetPlans, plan],
    }));
  };

  const planLabels: Record<string, string> = { trial: "试用版", basic: "基础版", standard: "标准版", premium: "旗舰版" };

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-lg font-bold text-gray-800">平台公告</h2>
          <p className="text-xs text-gray-400 mt-0.5">向所有或指定套餐租户发布平台级公告</p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white text-sm rounded-xl hover:bg-amber-400 cursor-pointer whitespace-nowrap"
        >
          <i className="ri-add-line"></i> 发布公告
        </button>
      </div>

      <div className="flex gap-5">
        <div className="flex-1 min-w-0 space-y-3">
          {notices.map((notice) => {
            const lvl = levelConfig[notice.level];
            return (
              <div
                key={notice.id}
                onClick={() => setSelectedNotice(notice)}
                className={`bg-white rounded-2xl p-5 cursor-pointer transition-all border-2 ${
                  selectedNotice?.id === notice.id ? "border-amber-400" : "border-transparent hover:border-gray-200"
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0 ${lvl.color}`}>
                    <i className={`${lvl.icon} text-sm`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <h4 className="text-sm font-semibold text-gray-800 leading-snug">{notice.title}</h4>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <span className={`text-xs px-1.5 py-0.5 rounded-full ${lvl.badge}`}>{lvl.label}</span>
                        <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                          notice.status === "published" ? "bg-green-50 text-green-700" : "bg-gray-100 text-gray-500"
                        }`}>
                          {notice.status === "published" ? "已发布" : "草稿"}
                        </span>
                      </div>
                    </div>
                    <p className="text-xs text-gray-400 line-clamp-1 mb-2">{notice.content}</p>
                    <div className="flex items-center gap-3 text-xs text-gray-400 flex-wrap">
                      <span>推送至：{notice.targetPlans.map((p) => planLabels[p]).join("、")} 租户</span>
                      {notice.status === "published" && (
                        <span className="flex items-center gap-1">
                          <i className="ri-eye-line"></i> {notice.readCount} 个租户已读
                        </span>
                      )}
                      {notice.publishedAt && <span>{notice.publishedAt}</span>}
                    </div>
                  </div>
                </div>

                {notice.status === "draft" && (
                  <div className="mt-3 pt-3 border-t border-gray-50 flex gap-2">
                    <button
                      onClick={(e) => { e.stopPropagation(); handlePublish(notice.id); }}
                      disabled={publishing}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-500 text-white text-xs rounded-xl hover:bg-amber-400 cursor-pointer transition-colors whitespace-nowrap disabled:opacity-50"
                    >
                      <i className="ri-send-plane-line"></i>
                      {publishing ? "发布中..." : "立即发布"}
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); setNotices((p) => p.filter((n) => n.id !== notice.id)); }}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 text-red-500 text-xs rounded-xl hover:bg-red-100 cursor-pointer transition-colors whitespace-nowrap"
                    >
                      <i className="ri-delete-bin-line"></i> 删除
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {selectedNotice && (
          <div className="w-80 flex-shrink-0">
            <div className="bg-white rounded-2xl p-5 sticky top-0">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-gray-800 text-sm">公告详情</h3>
                <button onClick={() => setSelectedNotice(null)} className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer rounded-lg hover:bg-gray-100">
                  <i className="ri-close-line"></i>
                </button>
              </div>
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <span className={`text-xs px-2 py-0.5 rounded-full ${levelConfig[selectedNotice.level].badge}`}>
                  {levelConfig[selectedNotice.level].label}
                </span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  selectedNotice.status === "published" ? "bg-green-50 text-green-700" : "bg-gray-100 text-gray-500"
                }`}>
                  {selectedNotice.status === "published" ? "已发布" : "草稿"}
                </span>
              </div>
              <h4 className="font-semibold text-gray-800 text-sm mb-3 leading-snug">{selectedNotice.title}</h4>
              <p className="text-xs text-gray-600 bg-gray-50 rounded-xl p-3 leading-relaxed mb-4">{selectedNotice.content}</p>
              <div className="space-y-2 text-xs">
                <div className="flex justify-between">
                  <span className="text-gray-400">推送对象</span>
                  <span className="text-gray-700 text-right max-w-[60%]">
                    {selectedNotice.targetPlans.map((p) => planLabels[p]).join("、")}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">创建时间</span>
                  <span className="text-gray-700">{selectedNotice.createdAt}</span>
                </div>
                {selectedNotice.publishedAt && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">发布时间</span>
                    <span className="text-gray-700">{selectedNotice.publishedAt}</span>
                  </div>
                )}
                {selectedNotice.status === "published" && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">已读租户</span>
                    <span className="text-green-600 font-medium">{selectedNotice.readCount} 个</span>
                  </div>
                )}
              </div>
              {selectedNotice.status === "draft" && (
                <button
                  onClick={() => handlePublish(selectedNotice.id)}
                  className="w-full mt-4 py-2.5 bg-amber-500 text-white text-sm rounded-xl hover:bg-amber-400 cursor-pointer transition-colors whitespace-nowrap"
                >
                  <i className="ri-send-plane-line mr-1.5"></i>立即发布此公告
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl w-[520px] max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-gray-100">
              <h3 className="font-bold text-gray-800">发布平台公告</h3>
              <button onClick={() => setShowCreate(false)} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer rounded-lg hover:bg-gray-100">
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-2">公告级别</label>
                <div className="flex gap-2">
                  {(Object.entries(levelConfig) as [PlatformNotice["level"], typeof levelConfig[PlatformNotice["level"]]][]).map(([key, cfg]) => (
                    <button
                      key={key}
                      onClick={() => setNewNotice((p) => ({ ...p, level: key }))}
                      className={`flex-1 flex items-center gap-1.5 justify-center py-2 rounded-xl border-2 text-xs cursor-pointer transition-all whitespace-nowrap ${
                        newNotice.level === key ? `border-amber-400 ${cfg.badge}` : "border-gray-100 text-gray-500 hover:border-gray-200"
                      }`}
                    >
                      <i className={cfg.icon}></i> {cfg.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">公告标题 *</label>
                <input
                  type="text"
                  value={newNotice.title}
                  onChange={(e) => setNewNotice((p) => ({ ...p, title: e.target.value }))}
                  placeholder="请输入公告标题"
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-amber-400"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">公告内容 *</label>
                <textarea
                  value={newNotice.content}
                  onChange={(e) => setNewNotice((p) => ({ ...p, content: e.target.value }))}
                  placeholder="请输入公告正文..."
                  rows={5}
                  maxLength={500}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-amber-400 resize-none"
                ></textarea>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-2">推送目标（套餐）</label>
                <div className="flex gap-2 flex-wrap">
                  {Object.entries(planLabels).map(([plan, label]) => (
                    <label key={plan} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl border-2 cursor-pointer text-xs transition-all select-none ${
                      newNotice.targetPlans.includes(plan) ? "border-amber-400 bg-amber-50 text-amber-700" : "border-gray-100 text-gray-500 hover:border-gray-200"
                    }`}>
                      <input
                        type="checkbox"
                        checked={newNotice.targetPlans.includes(plan)}
                        onChange={() => toggleTargetPlan(plan)}
                        className="hidden"
                      />
                      {label}
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 px-6 pb-6 pt-4 border-t border-gray-100">
              <button onClick={() => setShowCreate(false)} className="px-4 py-2 text-sm text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 cursor-pointer whitespace-nowrap">取消</button>
              <button
                onClick={() => {
                  if (!newNotice.title || !newNotice.content) return;
                  const n: PlatformNotice = {
                    id: `pn${Date.now()}`,
                    title: newNotice.title,
                    content: newNotice.content,
                    level: newNotice.level,
                    targetPlans: newNotice.targetPlans,
                    status: "published",
                    publishedAt: new Date().toLocaleString("zh-CN"),
                    createdAt: new Date().toISOString().slice(0, 10),
                    readCount: 0,
                  };
                  setNotices((p) => [n, ...p]);
                  setShowCreate(false);
                  setNewNotice({ title: "", content: "", level: "info", targetPlans: ["trial", "basic", "standard", "premium"] });
                }}
                className="px-4 py-2 text-sm text-white bg-amber-500 rounded-xl hover:bg-amber-400 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-send-plane-line mr-1.5"></i>立即发布
              </button>
              <button onClick={handleCreate} className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 cursor-pointer whitespace-nowrap">存草稿</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
